import { useCallback, useEffect, useMemo, useState } from 'react'

import SearchIcon from '@heroicons/react/outline/SearchIcon'
import clsx from 'clsx'
import { useLocation } from 'react-router-dom'

import { NetworkVisibility, RoleType } from '@tribeplatform/gql-client/types'
import { useStaticIntl } from '@tribeplatform/react-components/i18n'
import {
  SystemSpaceSlug,
  useAuthToken,
  useSpaceUtil,
} from '@tribeplatform/react-sdk/hooks'
import { useSlate } from '@tribeplatform/react-slate-kit/hooks'
import { TopBar } from '@tribeplatform/react-ui-kit/TopBar'

import { useMobileSidebar } from '../../MobileSidebarProvider'
import { SearchBar } from '../Search/SearchBar'
import { defaultNavbarControlItemsFactory, NavbarStyle } from './constants'
import type { NavbarControlItem, NavbarNavigationItem } from './constants'
import { NavbarControl } from './controls/NavbarControl'
import { NavbarControlOverride } from './controls/NavbarControlOverride'
import { NavbarNavigation } from './navigations/NavbarNavigation'
import { useMobileBottomNavigation } from './useMobileBottomNavigation'
import { useMobileNavbar } from './useMobileNavbar'
import { getCleanNavbarProps } from './utils'

export const Navbar = ({
  view: newView,
  navigationItems: newNavigationItems,
  controlItems,
}: {
  view: NavbarStyle
  navigationItems: NavbarNavigationItem[]
  controlItems?: NavbarControlItem[]
}) => {
  const { data: authToken } = useAuthToken()
  const { network, networkPublicInfo } = authToken || {}

  const { enabled: mobileBottomNavigationEnabled } = useMobileBottomNavigation()
  const { toggleMobileSidebarOpen, setNavigationItems } = useMobileSidebar()

  const {
    extraContext: {
      slate: { mobileSidebarEnable = true },
    },
  } = useSlate<{ mobileSidebarEnable: boolean }>()

  const guardNavigation =
    networkPublicInfo?.visibility === NetworkVisibility.private &&
    (!authToken?.member?.role?.type ||
      authToken?.member?.role?.type === RoleType.guest)

  const { view, navigationItems } = useMemo(
    () =>
      getCleanNavbarProps({
        network,
        props: { view: newView, navigationItems: newNavigationItems },
      }),
    [network, newView, newNavigationItems],
  )

  const defaultNavbarControls = useStaticIntl(defaultNavbarControlItemsFactory)
  const navbarControlItems = useMemo(() => {
    if (!controlItems) {
      return defaultNavbarControls
    }
    return controlItems
  }, [controlItems, defaultNavbarControls])

  useEffect(() => {
    setNavigationItems(navigationItems)
  }, [navigationItems?.length])

  const topLine =
    !guardNavigation && view === NavbarStyle.STACKED ? (
      <NavbarNavigation navigationItems={navigationItems} />
    ) : null

  const showSearchIcon =
    view === NavbarStyle.STACKED || !navigationItems?.length

  const searchInputMarkup = !guardNavigation && (
    <SearchBar autoFocus={!showSearchIcon} />
  )

  const center =
    navigationItems?.length && view === NavbarStyle.DEFAULT ? (
      <div className="flex flex-grow items-center space-s-3 justify-center">
        {!guardNavigation && (
          <NavbarNavigation navigationItems={navigationItems} />
        )}
      </div>
    ) : (
      <>{searchInputMarkup}</>
    )

  const { title } = useMobileNavbar()

  const { pathname } = useLocation()
  const { matchPath } = useSpaceUtil()
  const isSearchPage = !!matchPath(SystemSpaceSlug.SEARCH, pathname)

  const [searchOpen, setSearchOpen] = useState<boolean>(isSearchPage)
  const toggleSearchOpen = useCallback(
    () => setSearchOpen(searchOpen => !searchOpen),
    [],
  )

  const controls = (
    <NavbarControlOverride>
      {!guardNavigation && (
        <TopBar.Button
          onClick={toggleSearchOpen}
          className={clsx(
            showSearchIcon && 'lg:hidden',
            mobileBottomNavigationEnabled && 'hidden sm:flex',
          )}
        >
          <SearchIcon className="h-5 w-5" aria-hidden="true" />
        </TopBar.Button>
      )}
      <NavbarControl controlItems={navbarControlItems} />
    </NavbarControlOverride>
  )

  return (
    <TopBar
      topLine={topLine}
      center={center}
      controls={controls}
      mobileTitle={mobileBottomNavigationEnabled ? title : undefined}
      mobileSidebarEnable={mobileSidebarEnable as boolean}
      toggleMobileSidebar={toggleMobileSidebarOpen}
      searchOpen={searchOpen}
      toggleSearchOpen={toggleSearchOpen}
      searchInput={searchInputMarkup}
      bottomBarEnabled={mobileBottomNavigationEnabled}
    />
  )
}
